(() => {
  const requests = new WeakMap();
  const mediaScrollStates =
    new WeakMap();
  const activeMediaSlides = new WeakMap();
  const externalMediaStates = new WeakMap();
  const externalMediaWindows = new WeakMap();
  let externalMediaListenerReady = false;
  let externalMediaId = 0;
  const coverageTriggers = new WeakMap();
  const installDialogTriggers = new WeakMap();
  const spoksBackInStockObservers = new WeakMap();
  const friendlyVariantReservedParams = [
    "design",
    "finish",
    "coverage"
  ];

  const getRoot = (element) =>
    element?.closest?.("[data-sw-product-root]");

  function getMediaScrollState(track) {
    let state =
      mediaScrollStates.get(track);

    if (!state) {
      state = {
        settleTimer: null,
        userInitiated: false
      };

      mediaScrollStates.set(
        track,
        state
      );
    }

    return state;
  }

  /**
   * Convert Shopify image URLs into a stable comparison key.
   * Responsive image widths and query parameters are ignored.
   */
  function getImageKey(image) {
    if (!image) return "";

    const source =
      typeof image === "string"
        ? image
        : (
            image.currentSrc ||
            image.getAttribute("src") ||
            image.getAttribute("data-src") ||
            ""
          );

    if (!source) return "";

    try {
      const url = new URL(
        source,
        window.location.origin
      );

      let pathname = decodeURIComponent(
        url.pathname
      ).toLowerCase();

      pathname = pathname.replace(
        /_(?:\d+x\d+|\d+x|x\d+)(?=\.[a-z0-9]+$)/i,
        ""
      );

      return pathname;
    } catch (error) {
      return source
        .split("?")[0]
        .toLowerCase();
    }
  }

  /**
   * Find the visual or coverage option whose image matches
   * the selected gallery thumbnail.
   */
  function findMatchingOptionInput(
    root,
    mediaButton
  ) {
    if (!root || !mediaButton) {
      return null;
    }

    const thumbnailImage =
      mediaButton.querySelector("img");

    const thumbnailKey =
      getImageKey(thumbnailImage);

    if (!thumbnailKey) {
      return null;
    }

    const optionLabels =
      root.querySelectorAll(
        [
          ".swp__option-label--visual",
          ".swp__option-label--coverage"
        ].join(",")
      );

    for (const label of optionLabels) {
      const optionImage =
        label.querySelector("img");

      const storedSource =
        label.querySelector(
          ".swp__option-swatch"
        )?.dataset.swSourceImage || "";

      const optionKey =
        getImageKey(
          storedSource ||
          optionImage
        );

      if (
        optionKey &&
        optionKey === thumbnailKey
      ) {
        const inputId =
          label.getAttribute("for");

        if (!inputId) continue;

        const input =
          document.getElementById(inputId);

        if (
          input &&
          root.contains(input)
        ) {
          return input;
        }
      }
    }

    return null;
  }

  /**
   * Add the fields expected by Kalles' native ProductForm.
   */
  function prepareKallesFormFields(form) {
    if (!form) return;

    let actionInput =
      form.querySelector(
        'input[name="action_added"]'
      );

    if (!actionInput) {
      actionInput =
        document.createElement("input");

      actionInput.type = "hidden";
      actionInput.name = "action_added";

      form.appendChild(actionInput);
    }

    actionInput.value =
      "open_cart_drawer";

    let errorWrapper =
      form.querySelector(
        '[ref="addToCartTextError"]'
      );

    if (!errorWrapper) {
      errorWrapper =
        document.createElement("div");

      errorWrapper.className =
        "hdt-form__message-wrapper hdt-form-message--error";

      errorWrapper.setAttribute(
        "ref",
        "addToCartTextError"
      );

      errorWrapper.setAttribute(
        "role",
        "alert"
      );

      errorWrapper.hidden = true;

      errorWrapper.innerHTML = `
        <span
          class="hdt-error-message"
          aria-live="polite"
        ></span>
      `;

      const purchaseRow =
        form.querySelector(
          ".swp__purchase-row"
        );

      if (purchaseRow) {
        form.insertBefore(
          errorWrapper,
          purchaseRow
        );
      } else {
        form.prepend(errorWrapper);
      }
    }

    const variantInput =
      form.querySelector(
        'input[name="id"]'
      );

    if (variantInput) {
      variantInput.disabled = false;
    }
  }

  /**
   * Connect the custom product form to Kalles 5.4.2's native
   * AJAX ProductForm component.
   *
   * Important:
   * The form is moved into the wrapper while the wrapper is
   * detached from the document. The completed wrapper is then
   * inserted into the page. This allows Kalles to find the form
   * during connectedCallback().
   */
  function initializeKallesAjaxCart(root) {
    if (!root) return;

    const form =
      root.querySelector(
        "form.swp__form"
      );

    if (!form) return;

    prepareKallesFormFields(form);

    const currentWrapper =
      form.closest(
        "wrapp-hdt-pr-frm"
      );

    if (currentWrapper) {
      currentWrapper.style.display =
        "block";

      currentWrapper.dataset
        .swKallesCartReady = "true";

      return;
    }

    const parent =
      form.parentNode;

    if (!parent) return;

    const nextSibling =
      form.nextSibling;

    const wrapper =
      document.createElement(
        "wrapp-hdt-pr-frm"
      );

    wrapper.className =
      "swp__kalles-product-form";

    wrapper.style.display =
      "block";

    wrapper.dataset
      .swKallesCartReady = "true";

    /*
     * The wrapper is still detached here.
     * Put the form inside before connecting it to the page.
     */
    wrapper.appendChild(form);

    /*
     * Kalles now connects to a wrapper that already contains
     * the complete product form.
     */
    parent.insertBefore(
      wrapper,
      nextSibling
    );
  }

  function getExternalMediaSource(iframe) {
    try {
      const source = iframe.getAttribute("src");
      if (!source) return null;
      const url = new URL(source, window.location.origin);
      if (url.protocol !== "https:") return null;
      const youtube = [
        "youtube.com", "www.youtube.com",
        "youtube-nocookie.com", "www.youtube-nocookie.com"
      ].includes(url.hostname);
      if (!youtube && url.hostname !== "player.vimeo.com") return null;
      return { source, origin: url.origin, youtube };
    } catch (error) {
      return null;
    }
  }

  function sendExternalMediaMessage(state, message) {
    try {
      if (!state.iframe.isConnected ||
          state.iframe.closest("[data-sw-media-slide]") !== state.slide ||
          state.slide.closest("[data-sw-product-root]") !== state.root ||
          state.iframe.getAttribute("src") !== state.source ||
          state.iframe.contentDocument !== null) return false;
      state.iframe.contentWindow.postMessage(JSON.stringify(message), state.origin);
      return true;
    } catch (error) {
      // A removed/unavailable frame must not interrupt gallery navigation.
      return false;
    }
  }

  function flushExternalMediaPause(state) {
    if (!state.ready || state.active || !state.pendingPause ||
        activeMediaSlides.get(state.root) === state.slide) return;
    const message = state.youtube
      ? { event: "command", func: "pauseVideo", args: [] }
      : { method: "pause" };
    if (sendExternalMediaMessage(state, message)) state.pendingPause = false;
  }

  function handleExternalMediaMessage(event) {
    const state = event.source && externalMediaWindows.get(event.source);
    if (!state || event.origin !== state.origin ||
        !state.iframe.isConnected ||
        state.iframe.getAttribute("src") !== state.source) return;
    let message;
    try {
      message = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
    } catch (error) {
      return;
    }
    if (!message || typeof message !== "object") return;
    if (state.youtube && message.event === "readyToListen") {
      state.handshakeSent = false;
      requestExternalMediaReady(state);
      return;
    }
    const ready = state.youtube
      ? message.event === "onReady" || message.event === "alreadyInitialized" ||
        (message.event === "initialDelivery" &&
          Array.isArray(message.info?.apiInterface) &&
          message.info.apiInterface.includes("pauseVideo"))
      : message.event === "ready" || message.method === "ping";
    if (!ready) return;
    state.ready = true;
    flushExternalMediaPause(state);
  }

  // A handshake is not a playback command. Never send either to the initial
  // same-origin about:blank document of a lazy iframe. The player's own reply,
  // not iframe load alone, authorizes a queued pause. No SDK or polling needed.
  function requestExternalMediaReady(state) {
    if (!state.visited || state.ready || state.handshakeSent) return;
    try {
      if (state.iframe.contentDocument !== null) return;
    } catch (error) {
      return;
    }
    const message = state.youtube
      ? { event: "listening", id: state.id, channel: "widget" }
      : { method: "ping" };
    state.handshakeSent = sendExternalMediaMessage(state, message);
  }

  function getExternalMediaState(iframe) {
    if (externalMediaStates.has(iframe)) return externalMediaStates.get(iframe);
    const source = getExternalMediaSource(iframe);
    if (!source) return null;
    let playerWindow;
    try { playerWindow = iframe.contentWindow; } catch (error) { return null; }
    if (!playerWindow) return null;
    const slide = iframe.closest("[data-sw-media-slide]");
    const root = slide?.closest("[data-sw-product-root]");
    if (!root) return null;
    const state = {
      ...source, iframe, id: `sw-gallery-${++externalMediaId}`,
      slide, root,
      ready: false, active: false, visited: false,
      pendingPause: false, handshakeSent: false
    };
    externalMediaStates.set(iframe, state);
    externalMediaWindows.set(playerWindow, state);
    if (!externalMediaListenerReady) {
      window.addEventListener("message", handleExternalMediaMessage);
      externalMediaListenerReady = true;
    }
    iframe.addEventListener("load", () => {
      state.ready = false;
      state.handshakeSent = false;
      requestExternalMediaReady(state);
    });
    return state;
  }

  function activateExternalMedia(iframe) {
    const state = getExternalMediaState(iframe);
    if (!state) return;
    state.active = true;
    state.visited = true;
    state.pendingPause = false;
    requestExternalMediaReady(state);
  }

  // Only the outgoing slide can need pausing. Retain its player and playback
  // position, and cancel a queued pause if the customer returns before ready.
  function pauseExternalMedia(iframe) {
    const state = externalMediaStates.get(iframe);
    if (!state || !state.visited) return;
    state.active = false;
    state.pendingPause = true;
    flushExternalMediaPause(state);
  }

  function updateMediaPlayback(
    root,
    activeSlide
  ) {
    if (!root || !activeSlide) {
      return;
    }

    const previousSlide = activeMediaSlides.get(root);
    if (previousSlide !== activeSlide) {
      activeMediaSlides.set(root, activeSlide);
      previousSlide?.querySelectorAll("iframe").forEach(pauseExternalMedia);
      activeSlide.querySelectorAll("iframe").forEach(activateExternalMedia);
    }

    root
      .querySelectorAll(
        "[data-sw-media-slide]"
      )
      .forEach((item) => {
        const video =
          item.querySelector("video");

        if (!video) return;

        if (item === activeSlide) {
          if (
            video.preload === "none"
          ) {
            video.preload =
              "metadata";

            video.load();
          }

        } else if (!video.paused) {
          video.pause();
        }
      });
  }

  function selectMedia(
    root,
    mediaId,
    smooth = true
  ) {
    if (!root || !mediaId) return;

    const track =
      root.querySelector(
        "[data-sw-media-track]"
      );

    const slide =
      root.querySelector(
        `[data-sw-media-slide][data-media-id="${mediaId}"]`
      );

    if (
      !track ||
      !slide ||
      slide.hidden
    ) {
      return;
    }

    const scrollState =
      getMediaScrollState(track);

    /*
     * This movement was requested by the page (a swatch,
     * thumbnail, URL restore, or AJAX variant response). It
     * must never be interpreted as a customer gallery swipe.
     */
    scrollState.userInitiated = false;

    window.clearTimeout(
      scrollState.settleTimer
    );

    track.scrollTo({
      left: slide.offsetLeft,
      behavior: smooth
        ? "smooth"
        : "auto"
    });

    root
      .querySelectorAll(
        "[data-sw-media-slide]"
      )
      .forEach((item) => {
        item.classList.toggle(
          "is-active",
          item === slide
        );
      });

    updateMediaPlayback(
      root,
      slide
    );

    root
      .querySelectorAll(
        "[data-sw-media-target]"
      )
      .forEach((button) => {
        const active =
          button.dataset
            .swMediaTarget ===
          String(mediaId);

        button.classList.toggle(
          "is-active",
          active
        );

        button.setAttribute(
          "aria-current",
          active
            ? "true"
            : "false"
        );
      });

  }

  function initializeGallery(root) {
    if (
      !root ||
      root.dataset.swGalleryReady ===
        "true"
    ) {
      return;
    }

    const track =
      root.querySelector(
        "[data-sw-media-track]"
      );

    if (!track) return;

    root.dataset.swGalleryReady =
      "true";

    selectMedia(
      root,
      root.dataset.selectedMediaId,
      false
    );

    let frame;
    const scrollState =
      getMediaScrollState(track);

    const markUserScroll = () => {
      scrollState.userInitiated =
        true;
    };

    track.addEventListener(
      "pointerdown",
      markUserScroll,
      {
        passive: true
      }
    );

    track.addEventListener(
      "touchstart",
      markUserScroll,
      {
        passive: true
      }
    );

    track.addEventListener(
      "wheel",
      (event) => {
        if (
          Math.abs(event.deltaX) > 0 ||
          event.shiftKey
        ) {
          markUserScroll();
        }
      },
      {
        passive: true
      }
    );

    track.addEventListener(
      "keydown",
      (event) => {
        if (
          [
            "ArrowLeft",
            "ArrowRight",
            "Home",
            "End",
            "PageUp",
            "PageDown"
          ].includes(event.key)
        ) {
          markUserScroll();
        }
      }
    );

    track.addEventListener(
      "scroll",
      () => {
        window.cancelAnimationFrame(
          frame
        );

        frame =
          window.requestAnimationFrame(
            () => {
              const slides =
                Array.from(
                  track.querySelectorAll(
                    "[data-sw-media-slide]"
                  )
                ).filter(
                  (slide) =>
                    !slide.hidden
                );

              if (!slides.length) {
                return;
              }

              const active =
                slides.reduce(
                  (
                    closest,
                    slide
                  ) => {
                    const distance =
                      Math.abs(
                        slide.offsetLeft -
                          track.scrollLeft
                      );

                    return distance <
                      closest.distance
                      ? {
                          slide,
                          distance
                        }
                      : closest;
                  },
                  {
                    slide: slides[0],
                    distance:
                      Number
                        .POSITIVE_INFINITY
                  }
                ).slide;

              const mediaId =
                active.dataset.mediaId;

              slides.forEach(
                (slide) => {
                  slide.classList.toggle(
                    "is-active",
                    slide === active
                  );
                }
              );

              updateMediaPlayback(
                root,
                active
              );

              root
                .querySelectorAll(
                  "[data-sw-media-target]"
                )
                .forEach(
                  (button) => {
                    const selected =
                      button.dataset
                        .swMediaTarget ===
                      mediaId;

                    button.classList.toggle(
                      "is-active",
                      selected
                    );

                    button.setAttribute(
                      "aria-current",
                      selected
                        ? "true"
                        : "false"
                    );
                  }
                );

              /*
               * A customer swipe, horizontal wheel, or navigation
               * key can change the visible slide without clicking a
               * thumbnail. Wait for its momentum to settle, then
               * apply the matching option. selectMedia() clears the
               * user marker, so its smooth programmatic scroll can
               * never select an intermediate slide.
               */
              window.clearTimeout(
                scrollState.settleTimer
              );

              scrollState.settleTimer =
                window.setTimeout(
                  () => {
                    const shouldSelectVariant =
                      scrollState
                        .userInitiated;

                    scrollState
                      .userInitiated = false;

                    if (
                      !shouldSelectVariant
                    ) {
                      return;
                    }

                    const settledSlide =
                      root.querySelector(
                        `[data-sw-media-slide][data-media-id="${mediaId}"]`
                      );

                    if (
                      !settledSlide ||
                      !settledSlide.classList.contains(
                        "is-active"
                      )
                    ) {
                      return;
                    }

                    selectVariantFromThumbnail(
                      root,
                      settledSlide
                    );
                  },
                  160
                );
            }
          );
      },
      {
        passive: true
      }
    );
  }

  function initializeSeriesGallery(root) {
    if (!root) return;

    root
      .querySelectorAll(
        "[data-sw-series-gallery]"
      )
      .forEach((gallery) => {
        if (
          gallery.dataset
            .swSeriesGalleryReady ===
          "true"
        ) {
          return;
        }

        const track =
          gallery.querySelector(
            "[data-sw-series-track]"
          );

        const slides =
          Array.from(
            gallery.querySelectorAll(
              "[data-sw-series-slide]"
            )
          );

        const previous =
          gallery.querySelector(
            "[data-sw-series-previous]"
          );

        const next =
          gallery.querySelector(
            "[data-sw-series-next]"
          );

        const pagination =
          gallery.querySelector(
            "[data-sw-series-pagination]"
          );

        if (
          !track ||
          !slides.length ||
          !previous ||
          !next ||
          !pagination
        ) {
          return;
        }

        gallery.dataset
          .swSeriesGalleryReady =
          "true";

        const reducedMotion =
          window.matchMedia(
            "(prefers-reduced-motion: reduce)"
          );

        let positions = [];
        let activeIndex = 0;
        let scrollFrame;
        let resizeFrame;
        let dragging = false;
        let dragStartX = 0;
        let dragStartScroll = 0;

        function slidePosition(slide) {
          const trackRect =
            track.getBoundingClientRect();

          const slideRect =
            slide.getBoundingClientRect();

          return (
            slideRect.left -
            trackRect.left +
            track.scrollLeft
          );
        }

        function nearestPositionIndex() {
          return positions.reduce(
            (
              closestIndex,
              position,
              index
            ) => (
              Math.abs(
                position -
                track.scrollLeft
              ) <
              Math.abs(
                positions[
                  closestIndex
                ] -
                track.scrollLeft
              )
                ? index
                : closestIndex
            ),
            0
          );
        }

        function updateControls() {
          if (!positions.length) {
            return;
          }

          activeIndex =
            nearestPositionIndex();

          previous.disabled =
            activeIndex === 0;

          next.disabled =
            activeIndex ===
            positions.length - 1;

          pagination
            .querySelectorAll(
              "button"
            )
            .forEach(
              (button, index) => {
                const active =
                  index ===
                  activeIndex;

                button.classList.toggle(
                  "is-active",
                  active
                );

                if (active) {
                  button.setAttribute(
                    "aria-current",
                    "true"
                  );
                } else {
                  button.removeAttribute(
                    "aria-current"
                  );
                }
              }
            );
        }

        function goTo(index) {
          if (!positions.length) {
            return;
          }

          const targetIndex =
            Math.max(
              0,
              Math.min(
                index,
                positions.length - 1
              )
            );

          track.scrollTo({
            left:
              positions[targetIndex],
            behavior:
              reducedMotion.matches
                ? "auto"
                : "smooth"
          });
        }

        function renderPagination() {
          pagination.replaceChildren();

          positions.forEach(
            (_, index) => {
              const button =
                document.createElement(
                  "button"
                );

              button.type = "button";
              button.setAttribute(
                "aria-label",
                `Go to gallery position ${index + 1} of ${positions.length}`
              );

              button.addEventListener(
                "click",
                () => {
                  goTo(index);
                }
              );

              pagination.appendChild(
                button
              );
            }
          );
        }

        function rebuildPositions() {
          const maximum =
            Math.max(
              0,
              track.scrollWidth -
                track.clientWidth
            );

          const nextPositions = [];

          slides.forEach((slide) => {
            const position =
              Math.max(
                0,
                Math.min(
                  maximum,
                  slidePosition(
                    slide
                  )
                )
              );

            if (
              !nextPositions.some(
                (existing) =>
                  Math.abs(
                    existing -
                      position
                  ) < 3
              )
            ) {
              nextPositions.push(
                position
              );
            }
          });

          if (
            maximum > 2 &&
            !nextPositions.some(
              (position) =>
                Math.abs(
                  position -
                    maximum
                ) < 3
            )
          ) {
            nextPositions.push(
              maximum
            );
          }

          positions =
            nextPositions.sort(
              (first, second) =>
                first - second
            );

          const hasOverflow =
            positions.length > 1;

          previous.hidden =
            !hasOverflow;

          next.hidden =
            !hasOverflow;

          pagination.hidden =
            !hasOverflow;

          gallery.classList.toggle(
            "has-overflow",
            hasOverflow
          );

          renderPagination();
          updateControls();
        }

        function finishDrag() {
          if (!dragging) return;

          dragging = false;

          track.classList.remove(
            "is-dragging"
          );

          goTo(
            nearestPositionIndex()
          );
        }

        previous.addEventListener(
          "click",
          () => {
            goTo(
              activeIndex - 1
            );
          }
        );

        next.addEventListener(
          "click",
          () => {
            goTo(
              activeIndex + 1
            );
          }
        );

        track.addEventListener(
          "scroll",
          () => {
            window.cancelAnimationFrame(
              scrollFrame
            );

            scrollFrame =
              window.requestAnimationFrame(
                updateControls
              );
          },
          {
            passive: true
          }
        );

        track.addEventListener(
          "keydown",
          (event) => {
            if (
              event.key !==
                "ArrowLeft" &&
              event.key !==
                "ArrowRight"
            ) {
              return;
            }

            event.preventDefault();

            goTo(
              activeIndex +
                (
                  event.key ===
                  "ArrowRight"
                    ? 1
                    : -1
                )
            );
          }
        );

        track.addEventListener(
          "pointerdown",
          (event) => {
            if (
              event.pointerType !==
                "mouse" ||
              positions.length <= 1
            ) {
              return;
            }

            dragging = true;
            dragStartX =
              event.clientX;
            dragStartScroll =
              track.scrollLeft;

            track.classList.add(
              "is-dragging"
            );

            track.setPointerCapture(
              event.pointerId
            );
          }
        );

        track.addEventListener(
          "pointermove",
          (event) => {
            if (!dragging) return;

            const distance =
              event.clientX -
              dragStartX;

            if (
              Math.abs(distance) > 2
            ) {
              event.preventDefault();
            }

            track.scrollLeft =
              dragStartScroll -
              distance;
          }
        );

        track.addEventListener(
          "pointerup",
          finishDrag
        );

        track.addEventListener(
          "pointercancel",
          finishDrag
        );

        track.addEventListener(
          "lostpointercapture",
          finishDrag
        );

        track.addEventListener(
          "dragstart",
          (event) => {
            event.preventDefault();
          }
        );

        if (
          "ResizeObserver" in
          window
        ) {
          const resizeObserver =
            new ResizeObserver(
              () => {
                window.cancelAnimationFrame(
                  resizeFrame
                );

                resizeFrame =
                  window.requestAnimationFrame(
                    rebuildPositions
                  );
              }
            );

          resizeObserver.observe(
            track
          );
        } else {
          window.addEventListener(
            "resize",
            rebuildPositions,
            {
              passive: true
            }
          );
        }

        rebuildPositions();

        window.requestAnimationFrame(
          rebuildPositions
        );
      });
  }

  /**
   * Remove the previous scroll-arrow swatch markup.
   * The current grid and disclosure control are added below.
   */
  function removeOldSwatchControls(
    root
  ) {
    if (!root) return;

    root
      .querySelectorAll(
        "[data-sw-swatch-scroller]"
      )
      .forEach((scroller) => {
        const track =
          scroller.querySelector(
            ".swp__option-values--visual"
          );

        if (
          !track ||
          !scroller.parentNode
        ) {
          return;
        }

        scroller.parentNode.insertBefore(
          track,
          scroller
        );

        scroller.remove();
      });

    root
      .querySelectorAll(
        ".swp__swatch-nav"
      )
      .forEach((button) => {
        button.remove();
      });

    root
      .querySelectorAll(
        ".swp__option-values--visual"
      )
      .forEach((track) => {
        track.removeAttribute(
          "data-sw-swatch-track"
        );

        track.removeAttribute(
          "data-sw-scroller-ready"
        );

        track.scrollLeft = 0;
      });
  }

  function pauseFinishReels(root) {
    if (!root) return;

    root
      .querySelectorAll(
        "[data-sw-finish-reel-video]"
      )
      .forEach((video) => {
        if (!video.paused) {
          video.pause();
        }
      });
  }

  function initializeFinishReels(root) {
    if (!root) return;

    root
      .querySelectorAll(
        "[data-sw-finish-reels]"
      )
      .forEach((reels) => {
        if (
          reels.dataset
            .swFinishReelsReady ===
            "true"
        ) {
          return;
        }

        const cards = Array.from(
          reels.querySelectorAll(
            "[data-sw-finish-reel]"
          )
        );

        if (!cards.length) return;

        reels.dataset
          .swFinishReelsReady =
          "true";

        const setPlayingState = (
          card,
          playing
        ) => {
          card.classList.toggle(
            "is-playing",
            playing
          );

          const playButton =
            card.querySelector(
              "[data-sw-finish-reel-play]"
            );

          const video =
            card.querySelector(
              "[data-sw-finish-reel-video]"
            );

          if (!playButton || !video) {
            return;
          }

          const videoLabel =
            video.getAttribute(
              "aria-label"
            ) || "finish video";

          playButton.setAttribute(
            "aria-label",
            playing
              ? `Pause ${videoLabel}`
              : `Play ${videoLabel}`
          );

          playButton.setAttribute(
            "aria-pressed",
            String(playing)
          );

          playButton.classList.toggle(
            "is-playing",
            playing
          );
        };

        cards.forEach((card) => {
          const video =
            card.querySelector(
              "[data-sw-finish-reel-video]"
            );

          const playButton =
            card.querySelector(
              "[data-sw-finish-reel-play]"
            );

          if (!video || !playButton) {
            return;
          }

          const loadVideo = () => {
            if (
              video.dataset
                .swFinishReelLoaded ===
              "true"
            ) {
              return;
            }

            const sources = Array.from(
              video.querySelectorAll(
                "source[data-src]"
              )
            );

            if (!sources.length) {
              return;
            }

            sources.forEach((source) => {
              source.src =
                source.dataset.src;
              source.removeAttribute(
                "data-src"
              );
            });

            video.dataset
              .swFinishReelLoaded =
              "true";
            video.load();
          };

          const togglePlayback = () => {
            if (!video.paused) {
              video.pause();
              return;
            }

            cards.forEach(
              (otherCard) => {
                const otherVideo =
                  otherCard.querySelector(
                    "[data-sw-finish-reel-video]"
                  );

                if (
                  otherVideo &&
                  otherVideo !== video &&
                  !otherVideo.paused
                ) {
                  otherVideo.pause();
                }
              }
            );

            loadVideo();

            const playPromise =
              video.play();

            if (
              playPromise &&
              typeof playPromise.catch ===
                "function"
            ) {
              playPromise.catch(
                () => {
                  setPlayingState(
                    card,
                    false
                  );
                }
              );
            }
          };

          playButton.addEventListener(
            "click",
            togglePlayback
          );

          video.addEventListener(
            "click",
            togglePlayback
          );

          [
            "play",
            "pause",
            "ended",
            "emptied",
            "error"
          ].forEach((eventName) => {
            video.addEventListener(
              eventName,
              () => {
                setPlayingState(
                  card,
                  !video.paused &&
                    !video.ended
                );
              }
            );
          });
        });
      });
  }

  /**
   * Keep visual option groups compact while preserving the
   * currently selected design in the first ten swatches.
   */
  function initializeVisualSwatchLimits(
    root,
    expandedOptionKeys = new Set()
  ) {
    if (!root) return;

    root
      .querySelectorAll(
        ".swp__swatch-toggle"
      )
      .forEach((button) => {
        button.remove();
      });

    root
      .querySelectorAll(
        ".swp__option-values--visual"
      )
      .forEach((track, trackIndex) => {
        const labels = Array.from(
          track.querySelectorAll(
            ".swp__option-label--visual"
          )
        );

        const entries = labels.map(
          (label) => {
            const inputId =
              label.getAttribute("for");

            const input = inputId
              ? document.getElementById(
                  inputId
                )
              : null;

            return {
              input:
                input && root.contains(input)
                  ? input
                  : null,
              label
            };
          }
        );

        entries.forEach(
          ({ input, label }) => {
            label.hidden = false;

            if (input) {
              input.hidden = false;
            }
          }
        );

        if (entries.length <= 10) {
          return;
        }

        const selectedEntry =
          entries.find(
            ({ input }) =>
              input?.checked
          );

        const collapsedEntries =
          entries.slice(0, 10);

        if (
          selectedEntry &&
          !collapsedEntries.includes(
            selectedEntry
          )
        ) {
          collapsedEntries[9] =
            selectedEntry;
        }

        const collapsedSet = new Set(
          collapsedEntries
        );

        const rootKey = (
          root.id || "product"
        ).replace(
          /[^a-z0-9_-]/gi,
          "-"
        );

        if (!track.id) {
          track.id = `SWSwatches-${rootKey}-${trackIndex}`;
        }

        const button =
          document.createElement(
            "button"
          );

        button.type = "button";
        button.className =
          "swp__swatch-toggle";
        button.setAttribute(
          "aria-controls",
          track.id
        );

        const optionKey =
          track.closest(
            "[data-sw-swatch-option-key]"
          )?.dataset
            .swSwatchOptionKey || "";

        let expanded =
          expandedOptionKeys.has(
            optionKey
          );

        const render = () => {
          entries.forEach(
            (entry) => {
              const hidden =
                !expanded &&
                !collapsedSet.has(entry);

              entry.label.hidden = hidden;

              if (entry.input) {
                entry.input.hidden = hidden;
              }
            }
          );

          button.setAttribute(
            "aria-expanded",
            String(expanded)
          );

          button.textContent = expanded
            ? "Show less"
            : `View all (${entries.length})`;
        };

        button.addEventListener(
          "click",
          () => {
            expanded = !expanded;
            render();
          }
        );

        track.insertAdjacentElement(
          "afterend",
          button
        );

        render();
      });
  }

  function releaseCoverageDialog(dialog) {
    document.documentElement.classList.remove(
      "swp-has-coverage-dialog"
    );

    const triggerState =
      coverageTriggers.get(dialog);

    const trigger =
      triggerState?.button;

    coverageTriggers.delete(dialog);

    if (trigger?.isConnected) {
      trigger.focus({
        preventScroll: true
      });

      if (!triggerState.showFocusRing) {
        trigger.blur();
      }
    }
  }

  function initializeCoverageDialog(root) {
    const dialog =
      root?.querySelector(
        "[data-sw-coverage-dialog]"
      );

    if (
      !dialog ||
      dialog.dataset.swCoverageReady ===
        "true"
    ) {
      return;
    }

    dialog.dataset.swCoverageReady =
      "true";

    dialog.addEventListener(
      "cancel",
      () => {
        window.setTimeout(
          () => {
            releaseCoverageDialog(
              dialog
            );
          },
          0
        );
      }
    );

    dialog.addEventListener(
      "close",
      () => {
        releaseCoverageDialog(
          dialog
        );
      }
    );
  }

  function loadCoverageImage(dialog) {
    const image =
      dialog?.querySelector(
        "[data-sw-coverage-image]"
      );

    if (
      !image ||
      image.getAttribute("src")
    ) {
      return;
    }

    if (image.dataset.srcset) {
      image.setAttribute(
        "srcset",
        image.dataset.srcset
      );
    }

    if (image.dataset.src) {
      image.setAttribute(
        "src",
        image.dataset.src
      );
    }
  }

  function openCoverageDialog(
    button,
    showFocusRing = true
  ) {
    const root = getRoot(button);
    const dialog =
      root?.querySelector(
        "[data-sw-coverage-dialog]"
      );

    if (!dialog) return;

    initializeCoverageDialog(root);
    loadCoverageImage(dialog);

    coverageTriggers.set(
      dialog,
      {
        button,
        showFocusRing
      }
    );

    document.documentElement.classList.add(
      "swp-has-coverage-dialog"
    );

    if (
      typeof dialog.showModal ===
        "function"
    ) {
      if (!dialog.open) {
        dialog.showModal();
      }
    } else {
      dialog.setAttribute(
        "open",
        ""
      );

      dialog
        .querySelector(
          "[data-sw-coverage-close]"
        )
        ?.focus();
    }
  }

  function closeCoverageDialog(dialog) {
    if (!dialog) return;

    if (
      typeof dialog.close ===
        "function" &&
      dialog.open
    ) {
      dialog.close();
      return;
    }

    dialog.removeAttribute(
      "open"
    );

    releaseCoverageDialog(
      dialog
    );
  }

  function normalizeYouTubeId(value) {
    const candidate =
      String(value || "").trim();

    return /^[A-Za-z0-9_-]{6,20}$/.test(
      candidate
    )
      ? candidate
      : "";
  }

  function youtubeIdFromTrigger(button) {
    const directId =
      normalizeYouTubeId(
        button?.dataset.youtubeId
      );

    if (directId) return directId;

    const source = String(
      button?.dataset.youtubeUrl ||
        ""
    ).trim();

    if (!source) return "";

    try {
      const url = new URL(
        source,
        window.location.origin
      );

      const hostname =
        url.hostname
          .replace(/^www\./, "")
          .toLowerCase();

      let candidate = "";

      if (
        hostname === "youtu.be"
      ) {
        candidate =
          url.pathname
            .split("/")
            .filter(Boolean)[0] ||
          "";
      } else if (
        hostname === "youtube.com" ||
        hostname.endsWith(
          ".youtube.com"
        ) ||
        hostname ===
          "youtube-nocookie.com" ||
        hostname.endsWith(
          ".youtube-nocookie.com"
        )
      ) {
        candidate =
          url.searchParams.get("v") ||
          "";

        if (!candidate) {
          const parts =
            url.pathname
              .split("/")
              .filter(Boolean);

          if (
            [
              "embed",
              "shorts",
              "live"
            ].includes(parts[0])
          ) {
            candidate =
              parts[1] || "";
          }
        }
      }

      return normalizeYouTubeId(
        candidate
      );
    } catch (error) {
      const match = source.match(
        /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|shorts\/|live\/))([A-Za-z0-9_-]{6,20})/i
      );

      return normalizeYouTubeId(
        match?.[1]
      );
    }
  }

  function isYouTubeShort(button) {
    const source = String(
      button?.dataset.youtubeUrl ||
        ""
    ).trim();

    if (!source) return false;

    try {
      const url = new URL(
        source,
        window.location.origin
      );

      const hostname =
        url.hostname
          .replace(/^www\./, "")
          .toLowerCase();

      const isYouTubeHost =
        hostname === "youtube.com" ||
        hostname.endsWith(
          ".youtube.com"
        );

      const firstPathSegment =
        url.pathname
          .split("/")
          .filter(Boolean)[0]
          ?.toLowerCase();

      return (
        isYouTubeHost &&
        firstPathSegment === "shorts"
      );
    } catch (error) {
      return /youtube\.com\/shorts\//i.test(
        source
      );
    }
  }

  function selectedOptionText(root) {
    const values = [];

    root
      ?.querySelectorAll(
        "[data-sw-option-value]:checked"
      )
      .forEach((input) => {
        values.push(
          input.value || ""
        );

        const label =
          input.labels?.[0] ||
          (
            input.id
              ? root.querySelector(
                  `label[for="${CSS.escape(
                    input.id
                  )}"]`
                )
              : null
          );

        values.push(
          label?.textContent || ""
        );
      });

    root
      ?.querySelectorAll(
        "[data-sw-option-select]"
      )
      .forEach((select) => {
        values.push(
          select.selectedOptions?.[0]
            ?.textContent || ""
        );
      });

    return values
      .join(" ")
      .toLowerCase()
      .replace(
        /[^a-z0-9]+/g,
        " "
      )
      .trim();
  }

  function updateNakedJuiceNotice(
    root
  ) {
    const notice =
      root?.querySelector(
        "[data-sw-naked-juice-notice]"
      );

    if (!notice) return;

    const selectedOptions =
      selectedOptionText(root);

    const showNakedJuice =
      root.dataset.swApplicationType ===
        "wet" ||
      (root.dataset.swNakedSeries ===
        "true" &&
        /(^|\s)wet(?:\s+application)?(\s|$)/.test(
          selectedOptions
        ));

    notice.hidden =
      !showNakedJuice;
  }

  function updateApplicationType(
    root
  ) {
    if (
      !root ||
      root.dataset.swNakedSeries !==
        "true" ||
      !root.querySelector(
        "[data-sw-finish-guidance]"
      )
    ) {
      return;
    }

    const finishGroup =
      root.querySelector(
        '[data-sw-url-param="finish"]'
      );

    if (!finishGroup) return;

    const selectedFinish =
      finishGroup.querySelector(
        "[data-sw-option-value]:checked"
      );

    const selectedFinishText = [
      selectedFinish?.value || "",
      selectedFinish?.labels?.[0]
        ?.textContent || "",
      finishGroup.querySelector(
        "[data-sw-option-select]"
      )?.selectedOptions?.[0]
        ?.textContent || ""
    ]
      .join(" ")
      .toLowerCase()
      .replace(
        /[^a-z0-9]+/g,
        " "
      )
      .trim();

    root.dataset.swApplicationType =
      /(^|\s)wet(?:\s+application)?(\s|$)/.test(
        selectedFinishText
      )
        ? "wet"
        : "dry";
  }

  function updateInstallGuide(
    root,
    dialog
  ) {
    if (
      !dialog?.matches(
        "[data-sw-install-guide-dialog]"
      )
    ) {
      return;
    }

    const selectedOptions =
      selectedOptionText(root);

    const showWetGuide =
      root.dataset.swApplicationType ===
        "wet" ||
      (root.dataset.swNakedSeries ===
        "true" &&
        /(^|\s)wet(?:\s+application)?(\s|$)/.test(
          selectedOptions
        ));

    const standardGuide =
      dialog.querySelector(
        "[data-sw-install-guide-standard]"
      );

    const wetGuide =
      dialog.querySelector(
        "[data-sw-install-guide-wet]"
      );

    if (standardGuide) {
      standardGuide.hidden =
        showWetGuide;
    }

    if (wetGuide) {
      wetGuide.hidden =
        !showWetGuide;
    }

    const title =
      dialog.querySelector(
        "[data-sw-install-guide-title]"
      );

    const intro =
      dialog.querySelector(
        "[data-sw-install-guide-intro]"
      );

    if (title) {
      title.textContent =
        showWetGuide
          ? title.dataset.wetTitle
          : title.dataset
              .standardTitle;
    }

    if (intro) {
      intro.textContent =
        showWetGuide
          ? intro.dataset.wetIntro
          : intro.dataset
              .standardIntro;
    }
  }

  function releaseInstallDialog(dialog) {
    document.documentElement.classList.remove(
      "swp-has-install-dialog"
    );

    const iframe =
      dialog?.querySelector(
        "[data-sw-install-video]"
      );

    if (iframe) {
      iframe.removeAttribute(
        "src"
      );
    }

    dialog?.classList.remove(
      "is-youtube-short"
    );

    const trigger =
      installDialogTriggers.get(
        dialog
      );

    installDialogTriggers.delete(
      dialog
    );

    if (trigger?.isConnected) {
      trigger.focus({
        preventScroll: true
      });
    }
  }

  function initializeInstallDialogs(root) {
    root
      ?.querySelectorAll(
        "[data-sw-install-dialog]"
      )
      .forEach((dialog) => {
        if (
          dialog.dataset
            .swInstallDialogReady ===
          "true"
        ) {
          return;
        }

        dialog.dataset
          .swInstallDialogReady =
          "true";

        dialog.addEventListener(
          "cancel",
          () => {
            window.setTimeout(
              () => {
                releaseInstallDialog(
                  dialog
                );
              },
              0
            );
          }
        );

        dialog.addEventListener(
          "close",
          () => {
            releaseInstallDialog(
              dialog
            );
          }
        );
      });
  }

  function openInstallDialog(button) {
    const root = getRoot(button);
    const dialogId =
      button?.getAttribute(
        "aria-controls"
      );

    const dialog =
      dialogId
        ? document.getElementById(
            dialogId
          )
        : null;

    if (
      !root ||
      !dialog ||
      getRoot(dialog) !== root
    ) {
      return;
    }

    updateInstallGuide(
      root,
      dialog
    );

    if (
      button.matches(
        "[data-sw-install-video-open]"
      )
    ) {
      const youtubeId =
        youtubeIdFromTrigger(button);

      if (!youtubeId) {
        window.location.assign(
          button.dataset
            .videoFallback ||
            "/pages/install-videos"
        );
        return;
      }

      const iframe =
        dialog.querySelector(
          "[data-sw-install-video]"
        );

      if (!iframe) return;

      dialog.classList.toggle(
        "is-youtube-short",
        isYouTubeShort(button)
      );

      iframe.setAttribute(
        "src",
        `https://www.youtube-nocookie.com/embed/${encodeURIComponent(
          youtubeId
        )}?autoplay=1&rel=0`
      );
    }

    initializeInstallDialogs(root);

    installDialogTriggers.set(
      dialog,
      button
    );

    document.documentElement.classList.add(
      "swp-has-install-dialog"
    );

    if (
      typeof dialog.showModal ===
        "function"
    ) {
      if (!dialog.open) {
        dialog.showModal();
      }
    } else {
      dialog.setAttribute(
        "open",
        ""
      );

      dialog
        .querySelector(
          "[data-sw-install-dialog-close]"
        )
        ?.focus();
    }
  }

  function closeInstallDialog(dialog) {
    if (!dialog) return;

    if (
      typeof dialog.close ===
        "function" &&
      dialog.open
    ) {
      dialog.close();
      return;
    }

    dialog.removeAttribute(
      "open"
    );

    releaseInstallDialog(
      dialog
    );
  }

  function getSpoksBackInStockButton(
    mount
  ) {
    return mount
      ?.querySelector(
        "[data-spoks-bis-button]"
      )
      ?.shadowRoot
      ?.querySelector("button") || null;
  }

  function initializeSpoksBackInStock(
    root
  ) {
    const button =
      root.querySelector(
        "[data-sw-spoks-bis-open]"
      );

    const mount =
      root.querySelector(
        "[data-spoks-bis-trigger]"
      );

    const previous =
      spoksBackInStockObservers.get(
        root
      );

    if (
      !button ||
      !mount
    ) {
      previous?.observer.disconnect();
      spoksBackInStockObservers.delete(
        root
      );
      return;
    }

    if (
      previous?.mount === mount
    ) {
      button.hidden =
        !getSpoksBackInStockButton(
          mount
        );
      return;
    }

    previous?.observer.disconnect();

    const sync = () => {
      button.hidden =
        !getSpoksBackInStockButton(
          mount
        );
    };

    const observer =
      new MutationObserver(sync);

    observer.observe(
      mount,
      {
        childList: true
      }
    );

    spoksBackInStockObservers.set(
      root,
      {
        mount,
        observer
      }
    );

    sync();
  }

  function initializeRoot(root) {
    removeOldSwatchControls(root);
    initializeVisualSwatchLimits(root);
    updateApplicationType(root);
    updateNakedJuiceNotice(root);
    initializeCoverageDialog(root);
    initializeInstallDialogs(root);
    initializeGallery(root);
    initializeSeriesGallery(root);
    initializeFinishReels(root);
    initializeKallesAjaxCart(root);
    initializeSpoksBackInStock(root);

    if (
      window.Shopify
        ?.PaymentButton
        ?.init
    ) {
      window.Shopify
        .PaymentButton
        .init();
    }

    initializeFriendlyVariantUrl(root);
  }

  function friendlyVariantUrlsEnabled(
    root
  ) {
    return (
      root?.dataset
        .swFriendlyVariantUrls ===
      "true"
    );
  }

  function friendlyVariantGroups(root) {
    return Array.from(
      root.querySelectorAll(
        "[data-sw-url-param]"
      )
    );
  }

  function friendlyVariantParamKeys(
    root
  ) {
    return new Set([
      ...friendlyVariantReservedParams,
      ...friendlyVariantGroups(root)
        .map(
          (group) =>
            group.dataset
              .swUrlParam?.trim()
        )
        .filter(Boolean)
    ]);
  }

  function selectedVariantId(root) {
    return (
      root.querySelector(
        "[data-sw-product-info]"
      )?.dataset.selectedVariantId ||
      root.querySelector(
        '.swp__form input[name="id"]'
      )?.value ||
      ""
    );
  }

  function openZakekeProduct(button) {
    const root = getRoot(button);

    const currentUrl = new URL(
      window.location.href
    );

    const variantId =
      selectedVariantId(root) ||
      currentUrl.searchParams.get(
        "variant"
      ) ||
      "";

    let rootUrl =
      button.dataset.swRootUrl || "";

    if (rootUrl === "/") {
      rootUrl = "";
    }

    const zakekeUrl = new URL(
      rootUrl +
        (
          window.zakekeUrl ||
          "/apps/zakeke"
        ),
      document.location
    );

    const productId =
      button.dataset
        .swZakekeProductId || "";

    if (productId) {
      zakekeUrl.searchParams.set(
        "pid",
        productId
      );
    }

    if (variantId) {
      zakekeUrl.searchParams.set(
        "id",
        variantId
      );
    }

    const quantity =
      root?.querySelector(
        '.swp__info input[name="quantity"]'
      );

    if (quantity) {
      zakekeUrl.searchParams.set(
        "quantity",
        quantity.value
      );
    }

    window.location.href =
      zakekeUrl.toString();
  }

  function selectedFriendlyVariantEntries(
    root
  ) {
    const entries = [];
    const usedKeys = new Set();
    const groups =
      friendlyVariantGroups(root);

    if (!groups.length) {
      return null;
    }

    for (const group of groups) {
      const key =
        group.dataset
          .swUrlParam?.trim() || "";

      if (
        !key ||
        usedKeys.has(key)
      ) {
        return null;
      }

      usedKeys.add(key);

      const select =
        group.querySelector(
          "[data-sw-option-select]"
        );

      const selectedControl =
        select?.selectedOptions[0] ||
        group.querySelector(
          "[data-sw-option-value]:checked"
        );

      const value =
        selectedControl?.dataset
          .swUrlValue?.trim() || "";

      const controls = select
        ? Array.from(select.options)
        : Array.from(
            group.querySelectorAll(
              "[data-sw-option-value]"
            )
          );

      const matchingValues =
        controls.filter(
          (control) =>
            control.dataset
              .swUrlValue === value
        );

      if (
        !value ||
        matchingValues.length !== 1
      ) {
        return null;
      }

      entries.push([
        key,
        value
      ]);
    }

    return entries;
  }

  function resolveFriendlyVariantRequest(
    root,
    searchParams
  ) {
    const groups =
      friendlyVariantGroups(root);

    const groupsByKey = new Map();

    for (const group of groups) {
      const key =
        group.dataset
          .swUrlParam?.trim() || "";

      if (!key) continue;

      if (groupsByKey.has(key)) {
        return {
          status: "invalid"
        };
      }

      groupsByKey.set(
        key,
        group
      );
    }

    const hasUnsupportedReservedParam =
      friendlyVariantReservedParams.some(
        (key) =>
          searchParams.has(key) &&
          !groupsByKey.has(key)
      );

    if (hasUnsupportedReservedParam) {
      return {
        status: "invalid"
      };
    }

    const requestedGroups =
      Array.from(
        groupsByKey.entries()
      ).filter(([key]) =>
        searchParams.has(key)
      );

    if (!requestedGroups.length) {
      return {
        status: "none"
      };
    }

    const resolutions = [];

    for (const [
      key,
      group
    ] of requestedGroups) {
      const requestedValues =
        searchParams.getAll(key);

      if (
        requestedValues.length !== 1 ||
        !requestedValues[0]
      ) {
        return {
          status: "invalid"
        };
      }

      const select =
        group.querySelector(
          "[data-sw-option-select]"
        );

      const controls = select
        ? Array.from(select.options)
        : Array.from(
            group.querySelectorAll(
              "[data-sw-option-value]"
            )
          );

      const matchingControls =
        controls.filter(
          (control) =>
            control.dataset
              .swUrlValue ===
            requestedValues[0]
        );

      if (
        matchingControls.length !== 1
      ) {
        return {
          status: "invalid"
        };
      }

      resolutions.push({
        control:
          select ||
          matchingControls[0],
        valueControl:
          matchingControls[0]
      });
    }

    let changed = false;

    for (const resolution of resolutions) {
      if (
        resolution.control.matches(
          "[data-sw-option-select]"
        )
      ) {
        if (
          resolution.control.value !==
          resolution.valueControl.value
        ) {
          resolution.control.value =
            resolution.valueControl.value;

          changed = true;
        }
      } else if (
        !resolution.control.checked
      ) {
        resolution.control.checked = true;
        changed = true;
      }
    }

    const currentPath = new URL(
      root.dataset.productUrl,
      window.location.origin
    ).pathname;

    const crossProductResolution =
      resolutions.find(
        (resolution) => {
          const productUrl =
            resolution.valueControl
              .dataset.productUrl;

          if (!productUrl) {
            return false;
          }

          return (
            new URL(
              productUrl,
              window.location.origin
            ).pathname !== currentPath
          );
        }
      );

    return {
      status: "resolved",
      changed,
      input:
        crossProductResolution
          ?.control ||
        resolutions[
          resolutions.length - 1
        ].control
    };
  }

  function initializeFriendlyVariantUrl(
    root
  ) {
    if (
      !friendlyVariantUrlsEnabled(root) ||
      root.dataset
        .swFriendlyVariantInitialized ===
        "true"
    ) {
      return;
    }

    root.dataset
      .swFriendlyVariantInitialized =
      "true";

    const url = new URL(
      window.location.href
    );

    const variantId =
      selectedVariantId(root);

    const optionIds =
      selectedOptionValueIds(root);

    if (
      url.searchParams.has(
        "variant"
      ) ||
      url.searchParams.has(
        "option_values"
      )
    ) {
      updateBrowserUrl(
        root,
        variantId,
        optionIds
      );

      return;
    }

    const resolution =
      resolveFriendlyVariantRequest(
        root,
        url.searchParams
      );

    if (
      resolution.status === "none"
    ) {
      return;
    }

    if (
      resolution.status === "invalid"
    ) {
      updateBrowserUrl(
        root,
        variantId,
        optionIds,
        {
          forceNative: true
        }
      );

      return;
    }

    if (!resolution.changed) {
      updateBrowserUrl(
        root,
        variantId,
        optionIds
      );

      return;
    }

    updateProduct(
      resolution.input
    );
  }

  function selectedOptionValueIds(
    root
  ) {
    const radioIds =
      Array.from(
        root.querySelectorAll(
          "[data-sw-option-value]:checked"
        )
      )
        .map(
          (input) =>
            input.dataset
              .optionValueId
        )
        .filter(Boolean);

    const selectIds =
      Array.from(
        root.querySelectorAll(
          "[data-sw-option-select]"
        )
      )
        .map(
          (select) =>
            select.selectedOptions[0]
              ?.dataset
              .optionValueId ||
            select.value
        )
        .filter(Boolean);

    return [
      ...radioIds,
      ...selectIds
    ];
  }

  function selectedProductUrl(
    input,
    root
  ) {
    if (
      input.matches(
        "[data-sw-option-select]"
      )
    ) {
      return (
        input.selectedOptions[0]
          ?.dataset.productUrl ||
        root.dataset.productUrl
      );
    }

    return (
      input.dataset.productUrl ||
      root.dataset.productUrl
    );
  }

  function buildSelectionUrl(
    root,
    productUrl
  ) {
    const url = new URL(
      productUrl ||
        root.dataset.productUrl,
      window.location.origin
    );

    const ids =
      selectedOptionValueIds(root);

    if (
      friendlyVariantUrlsEnabled(root)
    ) {
      const currentUrl = new URL(
        window.location.href
      );

      const managedParams =
        friendlyVariantParamKeys(root);

      managedParams.add("variant");
      managedParams.add("option_values");
      managedParams.add("section_id");

      const copiedKeys = new Set();

      currentUrl.searchParams.forEach(
        (value, key) => {
          if (managedParams.has(key)) {
            return;
          }

          if (!copiedKeys.has(key)) {
            url.searchParams.delete(key);
            copiedKeys.add(key);
          }

          url.searchParams.append(
            key,
            value
          );
        }
      );

      url.hash = currentUrl.hash;
    }

    url.searchParams.set(
      "section_id",
      root.dataset.sectionId
    );

    if (ids.length) {
      url.searchParams.set(
        "option_values",
        ids.join(",")
      );
    }

    return {
      url,
      ids
    };
  }

  function updateBrowserUrl(
    root,
    variantId,
    optionIds,
    options = {}
  ) {
    const productUrl = new URL(
      root.dataset.productUrl,
      window.location.origin
    );

    const friendlyEnabled =
      friendlyVariantUrlsEnabled(root);

    const url = friendlyEnabled
      ? new URL(
          window.location.href
        )
      : new URL(productUrl);

    url.pathname = productUrl.pathname;

    url.searchParams.delete(
      "section_id"
    );

    url.searchParams.delete(
      "variant"
    );

    url.searchParams.delete(
      "option_values"
    );

    if (friendlyEnabled) {
      for (
        const key of
        friendlyVariantParamKeys(root)
      ) {
        url.searchParams.delete(key);
      }
    }

    const friendlyEntries =
      friendlyEnabled &&
      !options.forceNative
        ? selectedFriendlyVariantEntries(
            root
          )
        : null;

    if (friendlyEntries) {
      for (const [
        key,
        value
      ] of friendlyEntries) {
        url.searchParams.set(
          key,
          value
        );
      }
    } else if (variantId) {
      url.searchParams.set(
        "variant",
        variantId
      );
    } else if (optionIds.length) {
      url.searchParams.set(
        "option_values",
        optionIds.join(",")
      );
    }

    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`
    );
  }

  async function updateProduct(input) {
    const root = getRoot(input);

    if (!root) return;

    pauseFinishReels(root);

    const quantityInput =
      root.querySelector(
        '.swp__info input[name="quantity"]'
      );

    const preservedQuantity =
      quantityInput?.value ||
      root.dataset
        .swQuantityValue ||
      "";

    if (preservedQuantity) {
      root.dataset.swQuantityValue =
        preservedQuantity;
    }

    const expandedOptionKeys =
      new Set(
        Array.from(
          root.querySelectorAll(
            '.swp__swatch-toggle[aria-expanded="true"]'
          )
        )
          .map((button) => {
            const controlledTrack =
              document.getElementById(
                button.getAttribute(
                  "aria-controls"
                ) || ""
              );

            return (
              controlledTrack
                ?.closest(
                  "[data-sw-swatch-option-key]"
                )
                ?.dataset
                .swSwatchOptionKey || ""
            );
          })
          .filter(Boolean)
      );

    const requestedProductUrl =
      selectedProductUrl(
        input,
        root
      );

    const currentPath =
      new URL(
        root.dataset.productUrl,
        window.location.origin
      ).pathname;

    const requestedPath =
      new URL(
        requestedProductUrl,
        window.location.origin
      ).pathname;

    const {
      url,
      ids
    } = buildSelectionUrl(
      root,
      requestedProductUrl
    );

    if (
      requestedPath !== currentPath
    ) {
      url.searchParams.delete(
        "section_id"
      );

      window.location.assign(
        `${url.pathname}${url.search}${url.hash}`
      );

      return;
    }

    requests.get(root)?.abort();

    const controller =
      new AbortController();

    requests.set(
      root,
      controller
    );

    root.classList.add(
      "is-loading"
    );

    root.setAttribute(
      "aria-busy",
      "true"
    );

    try {
      const response =
        await fetch(
          url.toString(),
          {
            signal:
              controller.signal,
            headers: {
              "X-Requested-With":
                "XMLHttpRequest"
            }
          }
        );

      if (!response.ok) {
        throw new Error(
          `Product update failed: ${response.status}`
        );
      }

      const responseText =
        await response.text();

      if (
        controller.signal.aborted ||
        requests.get(root) !==
          controller
      ) {
        return;
      }

      const html =
        new DOMParser()
          .parseFromString(
            responseText,
            "text/html"
          );

      const newRoot =
        html.querySelector(
          "[data-sw-product-root]"
        );

      const newInfo =
        newRoot?.querySelector(
          "[data-sw-product-info]"
        );

      const currentInfo =
        root.querySelector(
          "[data-sw-product-info]"
        );

      if (
        !newRoot ||
        !newInfo ||
        !currentInfo
      ) {
        throw new Error(
          "Updated product markup was not found."
        );
      }

      currentInfo.replaceWith(
        newInfo
      );

      const newQuantityInput =
        newInfo.querySelector(
          'input[name="quantity"]'
        );

      if (
        newQuantityInput &&
        preservedQuantity
      ) {
        newQuantityInput.value =
          preservedQuantity;
      }

      removeOldSwatchControls(root);
      initializeVisualSwatchLimits(
        root,
        expandedOptionKeys
      );

      /*
       * Variant changes replace the product-information HTML.
       * Connect the newly rendered form to Kalles again.
       */
      initializeKallesAjaxCart(root);
      initializeSpoksBackInStock(root);

      root.dataset.selectedMediaId =
        newRoot.dataset
          .selectedMediaId || "";

      root.dataset.swNakedSeries =
        newRoot.dataset
          .swNakedSeries || "false";

      root.dataset.swApplicationType =
        newRoot.dataset
          .swApplicationType || "";

      updateApplicationType(
        root
      );

      updateNakedJuiceNotice(
        root
      );

      selectMedia(
        root,
        root.dataset.selectedMediaId,
        true
      );

      const variantId =
        newInfo.dataset
          .selectedVariantId || "";

      updateBrowserUrl(
        root,
        variantId,
        ids
      );

      if (
        window.Shopify
          ?.PaymentButton
          ?.init
      ) {
        window.Shopify
          .PaymentButton
          .init();
      }
    } catch (error) {
      if (
        controller.signal.aborted ||
        requests.get(root) !==
          controller ||
        error.name ===
        "AbortError"
      ) {
        return;
      }

      console.error(error);

      url.searchParams.delete(
        "section_id"
      );

      window.location.assign(
        `${url.pathname}${url.search}${url.hash}`
      );
    } finally {
      if (
        requests.get(root) ===
        controller
      ) {
        requests.delete(root);

        root.classList.remove(
          "is-loading"
        );

        root.removeAttribute(
          "aria-busy"
        );
      }
    }
  }

  function selectVariantFromThumbnail(
    root,
    mediaButton
  ) {
    const optionInput =
      findMatchingOptionInput(
        root,
        mediaButton
      );

    if (
      !optionInput ||
      optionInput.checked
    ) {
      return;
    }

    optionInput.checked = true;

    updateProduct(optionInput);
  }

  document.addEventListener(
    "click",
    (event) => {
      const spoksBackInStockOpen =
        event.target.closest(
          "[data-sw-spoks-bis-open]"
        );

      if (spoksBackInStockOpen) {
        event.preventDefault();

        const root =
          getRoot(
            spoksBackInStockOpen
          );

        const mount =
          root?.querySelector(
            "[data-spoks-bis-trigger]"
          );

        getSpoksBackInStockButton(
          mount
        )?.click();

        return;
      }

      const installOpen =
        event.target.closest(
          "[data-sw-install-dialog-open]"
        );

      const zakekeOpen =
        event.target.closest(
          "[data-sw-zakeke-open]"
        );

      if (zakekeOpen) {
        event.preventDefault();
        event.stopPropagation();
        openZakekeProduct(zakekeOpen);
        return;
      }

      if (installOpen) {
        event.preventDefault();
        openInstallDialog(
          installOpen
        );
        return;
      }

      const installClose =
        event.target.closest(
          "[data-sw-install-dialog-close]"
        );

      if (installClose) {
        closeInstallDialog(
          installClose.closest(
            "[data-sw-install-dialog]"
          )
        );
        return;
      }

      if (
        event.target.matches(
          "[data-sw-install-dialog]"
        )
      ) {
        closeInstallDialog(
          event.target
        );
        return;
      }

      const coverageOpen =
        event.target.closest(
          "[data-sw-coverage-open]"
        );

      if (coverageOpen) {
        openCoverageDialog(
          coverageOpen,
          event.detail === 0
        );
        return;
      }

      const coverageClose =
        event.target.closest(
          "[data-sw-coverage-close]"
        );

      if (coverageClose) {
        closeCoverageDialog(
          coverageClose.closest(
            "[data-sw-coverage-dialog]"
          )
        );
        return;
      }

      if (
        event.target.matches(
          "[data-sw-coverage-dialog]"
        )
      ) {
        closeCoverageDialog(
          event.target
        );
        return;
      }

      const mediaButton =
        event.target.closest(
          "[data-sw-media-target]"
        );

      if (mediaButton) {
        const root =
          getRoot(mediaButton);

        selectMedia(
          root,
          mediaButton.dataset
            .swMediaTarget,
          true
        );

        selectVariantFromThumbnail(
          root,
          mediaButton
        );

        return;
      }

      const minus =
        event.target.closest(
          "[data-sw-quantity-minus]"
        );

      const plus =
        event.target.closest(
          "[data-sw-quantity-plus]"
        );

      if (minus || plus) {
        const quantity =
          event.target.closest(
            "[data-sw-quantity]"
          );

        const input =
          quantity?.querySelector(
            'input[type="number"]'
          );

        if (!input) return;

        const minimum = Number(
          input.min || 1
        );

        const current = Number(
          input.value || minimum
        );

        input.value = String(
          plus
            ? current + 1
            : Math.max(
                minimum,
                current - 1
              )
        );

        input.dispatchEvent(
          new Event(
            "change",
            {
              bubbles: true
            }
          )
        );
      }
    }
  );

  document.addEventListener(
    "keydown",
    (event) => {
      if (
        event.key !== "Escape"
      ) {
        return;
      }

      const installDialog =
        document.querySelector(
          "[data-sw-install-dialog][open]"
        );

      if (
        installDialog &&
        typeof installDialog.showModal !==
          "function"
      ) {
        closeInstallDialog(
          installDialog
        );
        return;
      }

      const coverageDialog =
        document.querySelector(
          "[data-sw-coverage-dialog][open]"
        );

      if (
        coverageDialog &&
        typeof coverageDialog.showModal !==
          "function"
      ) {
        closeCoverageDialog(
          coverageDialog
        );
        return;
      }

    }
  );

  document.addEventListener(
    "change",
    (event) => {
      if (
        event.target.matches(
          [
            "[data-sw-option-value]",
            "[data-sw-option-select]"
          ].join(",")
        )
      ) {
        updateProduct(
          event.target
        );
      }
    }
  );

  document.addEventListener(
    "DOMContentLoaded",
    () => {
      document
        .querySelectorAll(
          "[data-sw-product-root]"
        )
        .forEach(
          initializeRoot
        );
    }
  );

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      event.target
        .querySelectorAll(
          "[data-sw-product-root]"
        )
        .forEach(
          initializeRoot
        );
    }
  );
})();
